import React, { useMemo, useState } from "react";
import { Alert, Card, Col, Container, Form, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import Breadcrumbs from "../../../Common/Breadcrumbs";
import ShimmerLoader from "../../../Common/ShimmerLoader";
import Pagination from "../../ShopServices/Orders/components/Pagination";

import { useClaimRequests } from "./useClaimRequests";
import ClaimRequestsTable from "./components/ClaimRequestsTable";
import ConfirmActionModal from "./components/ConfirmActionModal";
import type { ClaimRequest } from "../../../api/playerVault";

import "../../Ads/Creatives/Creatives.css";
import "./ClaimRequests.css";

type ActionType = "approve" | "reject";

interface PendingAction {
  type: ActionType;
  request: ClaimRequest;
}

const ClaimRequestsPage: React.FC = () => {
  const navigate = useNavigate();
  const { functions, values, loading } = useClaimRequests();

  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [actionSubmitting, setActionSubmitting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const pageSize = values.params.pageSize ?? 20;
  const currentPage = values.params.pageNum ?? 1;
  const totalCount = values.totalCount ?? 0;
  const formattedTotal = useMemo(() => totalCount, [totalCount]);

  const handleApproveClick = (request: ClaimRequest) => {
    setPendingAction({ type: "approve", request });
  };

  const handleRejectClick = (request: ClaimRequest) => {
    setRejectReason("");
    setPendingAction({ type: "reject", request });
  };

  const handleConfirm = async () => {
    if (!pendingAction) return;
    setActionSubmitting(true);
    try {
      if (pendingAction.type === "approve") {
        await functions.approve(pendingAction.request.request_id);
        toast.success("Claim request approved successfully.");
      } else {
        await functions.reject(pendingAction.request.request_id, rejectReason);
        toast.success("Claim request rejected.");
      }
      setPendingAction(null);
      setRejectReason("");
    } catch (err: any) {
      toast.error(err?.message ?? "Action failed. Please try again.");
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (!actionSubmitting) {
      setPendingAction(null);
      setRejectReason("");
    }
  };

  const confirmTitle =
    pendingAction?.type === "approve" ? "Approve Request" : "Reject Request";
  const confirmMessage =
    pendingAction?.type === "approve"
      ? "Are you sure you want to approve this claim request? The player will be linked to the requesting user."
      : "Are you sure you want to reject this claim request? This action cannot be undone.";
  const confirmLabel =
    pendingAction?.type === "approve" ? "Approve" : "Reject";
  const confirmVariant =
    pendingAction?.type === "approve" ? "success" : "danger";

  return (
    <div className="page-content">
      <Container fluid>
        <div className="mb-3" style={{ padding: "0 15px" }}>
          <Breadcrumbs
            items={[
              { label: "Player Vault", onClick: () => navigate("/player-vault/claim-requests") },
              { label: "Claim Requests", active: true },
            ]}
          />
        </div>

        <div className="mb-4" style={{ padding: "0 15px" }}>
          <h1 className="ads-page-title">Claim Requests</h1>
          <p className="ads-page-subtitle">Total {formattedTotal} Requests</p>
        </div>

        <Row>
          <Col>
            <Card className="ads-page-card">
              <Card.Body className="ads-page-card__body">
                <div className="ads-toolbar">
                  <div />
                  <Form.Select
                    value={values.params.status ?? ""}
                    onChange={(e) =>
                      functions.setStatus(
                        (e.target.value as any) || undefined
                      )
                    }
                    style={{ width: 160 }}
                    aria-label="Filter by status"
                  >
                    <option value="">All Statuses</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="pending">Pending</option>
                  </Form.Select>
                </div>
                {loading.listLoading ? (
                  <ShimmerLoader type="table" rows={6} />
                ) : values.requests.length > 0 ? (
                  <ClaimRequestsTable
                    requests={values.requests}
                    onApprove={handleApproveClick}
                    onReject={handleRejectClick}
                  />
                ) : (
                  <div className="ads-empty">
                    <p className="ads-empty__text">No claim requests found.</p>
                  </div>
                )}
              </Card.Body>
            </Card>

            {!loading.listLoading && totalCount > 0 && values.totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={values.totalPages}
                totalCount={totalCount}
                pageSize={pageSize}
                onPageChange={(page) => functions.setPageNum(page)}
                onPageSizeChange={(size) => functions.setPageSize(size)}
              />
            )}
          </Col>
        </Row>

        {values.error && (
          <div style={{ padding: "0 15px" }}>
            <Alert
              variant="danger"
              dismissible
              onClose={() => functions.fetchList()}
            >
              {values.error}
            </Alert>
          </div>
        )}
      </Container>

      <ConfirmActionModal
        show={!!pendingAction}
        title={confirmTitle}
        message={confirmMessage}
        confirmLabel={confirmLabel}
        confirmVariant={confirmVariant}
        submitting={actionSubmitting}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        {...(pendingAction?.type === "reject" && {
          inputLabel: "Rejection Reason",
          inputPlaceholder: "Explain why this claim request is being rejected…",
          inputValue: rejectReason,
          onInputChange: setRejectReason,
          inputRequired: true,
        })}
      />
    </div>
  );
};

export default ClaimRequestsPage;
